/**
 * Provenance of the last clause-preview derivation (previewExtraction):
 * status, option/guidance counts, and — for partial runs — exactly which
 * clauses failed, so an admin knows whether previews reflect the active
 * document without diffing them by hand.
 */

import type { PreviewExtraction } from '../../types/orgTemplate';

const STATUS_TONES: Record<PreviewExtraction['status'], string> = {
  complete:
    'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
  partial: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  'skipped-no-tags': 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
  superseded: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
};

export default function PreviewExtractionDetail({
  extraction,
}: {
  extraction: PreviewExtraction;
}) {
  return (
    <div className="text-body-sm text-slate-600 dark:text-slate-400">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center rounded px-2 py-0.5 text-caption font-medium ${STATUS_TONES[extraction.status]}`}
        >
          {extraction.status}
        </span>
        <span>
          <span className="font-medium">{extraction.optionsUpdated}</span> options updated
        </span>
        <span>
          <span className="font-medium">{extraction.optionsCleared}</span> cleared
        </span>
        <span>
          <span className="font-medium">{extraction.guidanceBlocks}</span> guidance blocks
        </span>
        <span className="text-caption text-slate-400 dark:text-slate-500">
          from v{extraction.sourceVersion},{' '}
          {new Date(extraction.extractedAt).toLocaleDateString()}
        </span>
      </div>
      {extraction.failedClauseIds && extraction.failedClauseIds.length > 0 && (
        <ul className="mt-1 list-disc pl-5 font-mono text-caption text-amber-700 dark:text-amber-400">
          {extraction.failedClauseIds.map((clauseId) => (
            <li key={clauseId}>{clauseId}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
